import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { waLink } from "../data/siteConfig";
import { WhatsAppIcon } from "./WhatsAppIcon";

const Navbar = () => {
  const { t, toggle } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="sticky-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-ivory/85 shadow-[0_8px_30px_rgba(28,27,26,0.06)] backdrop-blur-md"
          : "bg-ivory/60 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <a
          href="#home"
          data-testid="nav-brand-link"
          className="font-head text-xl sm:text-2xl text-ink"
        >
          {t.brand.name}
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {t.nav.links.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              data-testid={`nav-link-${link.id}`}
              className="text-sm font-medium text-ink/70 transition-colors duration-300 hover:text-bronze-dark"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggle}
            data-testid="language-switcher"
            className="rounded-full border border-ink/15 px-4 py-2 text-sm font-semibold text-ink transition-colors duration-300 hover:border-bronze hover:text-bronze-dark"
            aria-label="Switch language"
          >
            {t.nav.switchTo}
          </button>
          <a
            href={waLink(t.hero.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden items-center gap-2 rounded-full bg-bronze px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-bronze-dark sm:inline-flex"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {t.nav.cta}
          </a>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label={t.nav.menuLabel}
            aria-expanded={open}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-line bg-ivory/95 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {t.nav.links.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  data-testid={`mobile-nav-link-${link.id}`}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-4 py-3 text-base font-medium text-ink transition-colors duration-300 hover:bg-sand hover:text-bronze-dark"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={waLink(t.hero.waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp-cta"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-bronze px-5 py-3 text-sm font-semibold text-white"
              >
                <WhatsAppIcon className="h-4 w-4" />
                {t.nav.cta}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
